import { buildApiParams } from '@/lib/filterRegistry';
import type { FilterConfig, FilterNegations, FilterOption, FilterValue } from '@/types/filters.types';
import type { SurgeryListParams, SurgerySearchType } from '@/services/surgery.service';
import { getErrorMessage } from '@/services/apiClient';

/**
 * Every sortable column key on the Surgery worklist, in header order.
 *
 * These are a CONTRACT WITH THE SERVER. `DataTable` sends a column's `key`
 * verbatim as the sort key, and SurgeryListService::sortColumn() falls back to
 * the default for anything it does not recognise — so a rename on either side
 * does not error, it silently stops sorting while the header keeps showing its
 * arrow. Four of these drifted exactly that way and shipped.
 *
 * surgerySortKeys.test.ts reads the PHP service and asserts the two lists still
 * match, which is the only check that spans the seam.
 */
export const SURGERY_SORT_KEYS = [
  'procedureDate',
  'patientName',
  'procedures',
  'doctorName',
  'coordinatorName',
  'labsReviewed',
  'medicalClearance',
  'due',
  'consultStatus',
  'note',
  'lastUpdatedAt',
] as const;

export type SurgerySortKey = (typeof SURGERY_SORT_KEYS)[number];

/**
 * The Surgery worklist's filter sidebar.
 *
 * Keys and option values mirror the legacy filter includes one-for-one
 * (html/html/lib/filters/filter-consult-*.php), because dynamic lists persist
 * these values. Do not rename a key or reword an option value.
 *
 * Legacy titles, in legacy order: Proc Date, Doctor, Coordinator, Facility,
 * Consult Status, Medical Clearance. The CRNA filter exists in the legacy code
 * but is commented out of the page, so it is not offered here either.
 */
export function buildSurgeryFilterConfigs(
  doctorLabel: string,
  coordLabel: string,
): FilterConfig[] {
  return [
    { key: 'search', label: 'Search', type: 'search', allLabel: 'Search cases...' },
    { key: 'proc-daterange', label: 'Proc Date', type: 'daterange' },
    {
      key: 'doctor',
      label: doctorLabel,
      type: 'select',
      allLabel: `All ${doctorLabel.toLowerCase()}s`,
      multi: true,
    },
    {
      key: 'coordinator',
      label: coordLabel,
      type: 'select',
      allLabel: `All ${coordLabel.toLowerCase()}s`,
      multi: true,
    },
    { key: 'facility', label: 'Facility', type: 'select', allLabel: 'All facilities', multi: true },
    {
      key: 'consult-status',
      label: 'Consult Status',
      type: 'select',
      allLabel: 'All statuses',
      multi: true,
    },
    {
      key: 'consult-clearance',
      label: 'Medical Clearance',
      type: 'select',
      allLabel: 'All clearances',
      multi: true,
    },
  ];
}

/** The sidebar's filter keys. Labels vary by account; keys do not. */
const SURGERY_FILTER_KEYS: ReadonlySet<string> = new Set(
  buildSurgeryFilterConfigs('Doctor', 'Coordinator').map((c) => c.key),
);

/** Paging, sort and search mode — everything the page sends besides filters. */
export interface SurgeryListBase {
  page: number;
  perPage: number;
  sort: string;
  order: 'asc' | 'desc';
  searchType: SurgerySearchType;
}

/**
 * The parameters the Surgery worklist sends, to GET /surgery and, unchanged,
 * to the export.
 *
 * Built on the shared `buildApiParams`, so the wire shape is every list page's:
 * one `filtersArray` JSON string with negations under `filter-plus`, and
 * `search` beside it. Only the sidebar's own keys are sent, so a value another
 * list left in browser storage cannot ride along.
 *
 * The server's half of this contract is SurgeryListService::FILTER_KEYS;
 * surgeryFilterKeys.test.ts reads it to prove the two agree.
 */
export function buildSurgeryListParams(
  values: Record<string, FilterValue>,
  negations: FilterNegations,
  base: SurgeryListBase,
): SurgeryListParams {
  const ownValues: Record<string, FilterValue> = {};
  for (const [key, value] of Object.entries(values)) {
    if (SURGERY_FILTER_KEYS.has(key)) ownValues[key] = value;
  }

  // A Consult Status saved before "Complete" was taken off the sidebar would
  // otherwise keep emptying the list with no way to clear it there.
  const statuses = ownValues['consult-status'];
  if (Array.isArray(statuses)) {
    ownValues['consult-status'] = statuses.filter((s) => CONSULT_STATUS_VALUES.has(s));
  }

  const { filtersArray, search } = buildApiParams(ownValues, negations);

  return search ? { ...base, filtersArray, search } : { ...base, filtersArray };
}

/**
 * Consult status values, exactly as legacy stores them in
 * `consult_requests.status` (filter-consult-status.php).
 *
 * `Complete` is deliberately absent: the worklist only lists open cases and its
 * base scope excludes `status = 'Complete'`, so that option could never match.
 */
export const CONSULT_STATUS_OPTIONS: FilterOption[] = [
  { value: 'Pre-Consult', label: 'Pre-Consult' },
  { value: 'Consult Ready', label: 'Consult Ready' },
  { value: 'Approved', label: 'Approved' },
  { value: 'QBS Required', label: 'QBS Required' },
  { value: 'Virtual Consult', label: 'Virtual Consult' },
  { value: 'Not Approved', label: 'Not Approved' },
];

const CONSULT_STATUS_VALUES: ReadonlySet<string> = new Set(CONSULT_STATUS_OPTIONS.map((o) => String(o.value)));

/**
 * Medical clearance values, exactly as legacy stores them in
 * `consult_requests.clearance` (filter-consult-clearance.php).
 */
export const MEDICAL_CLEARANCE_OPTIONS: FilterOption[] = [
  { value: 'Not Needed', label: 'Not Needed' },
  { value: 'Required', label: 'Required' },
  { value: 'In Process', label: 'In Process' },
  { value: 'Further Workup', label: 'Further Workup' },
  { value: 'Cleared', label: 'Cleared' },
  { value: 'Not Cleared', label: 'Not Cleared' },
  { value: 'Postponed', label: 'Postponed' },
];

/**
 * True when the request narrows the list: a search term, or any filter that
 * reaches the server. Read from the built params rather than the sidebar
 * values, so "filters are active" means exactly what the server was sent.
 */
export function hasActiveSurgeryFilters(params: SurgeryListParams): boolean {
  if (params.search) return true;
  try {
    const sent = JSON.parse(params.filtersArray || '{}') as Record<string, unknown>;
    return Object.keys(sent).length > 0;
  } catch {
    return false;
  }
}

/** What the worklist body shows in place of, or inside, the table. */
export type SurgeryListView =
  | { kind: 'refused'; title: string; message: string }
  | { kind: 'failed'; title: string; message: string }
  | { kind: 'table'; emptyTitle: string; emptyDescription: string };

/**
 * What to do after a Surgery action fails for no reason the reader can fix:
 * the toast description (and worklist failure text) used when the server
 * sends no readable message of its own.
 */
export const SURGERY_RETRY_OR_SUPPORT = 'Try again. If it keeps happening, contact Prospera support.';

/** Fallback when a refusal arrives without a readable server message. */
export const SURGERY_REFUSED_FALLBACK =
  'You do not have access to Surgery. Ask your administrator to give you the Surgeries permission.';

function responseStatus(error: unknown): number | undefined {
  if (typeof error !== 'object' || error === null) return undefined;
  const status = (error as { response?: { status?: unknown } }).response?.status;
  return typeof status === 'number' ? status : undefined;
}

/**
 * Decides what the worklist body shows for a query state.
 *
 * A failed request must never fall through to the empty state: an empty list
 * reads as "no cases", which is false when the list simply did not load.
 *
 * - 403: the server refuses on two grounds (module off for the practice, or no
 *   legacy permission 6 — SurgeryController::authorizeSurgery), and its message
 *   already names the remedy, so it is shown as sent.
 * - Anything else: retry copy; the page renders a Retry button beside it.
 * - Otherwise the table renders, with an empty state that says whether the
 *   filters are the reason it is empty.
 */
export function surgeryListView(state: {
  isError: boolean;
  error: unknown;
  hasActiveFilters: boolean;
}): SurgeryListView {
  if (state.isError) {
    if (responseStatus(state.error) === 403) {
      return {
        kind: 'refused',
        title: "Surgery isn't available",
        message: getErrorMessage(state.error, SURGERY_REFUSED_FALLBACK),
      };
    }
    return {
      kind: 'failed',
      title: 'Could not load surgical cases',
      message: SURGERY_RETRY_OR_SUPPORT,
    };
  }

  return state.hasActiveFilters
    ? {
        kind: 'table',
        emptyTitle: 'No surgical cases match these filters',
        emptyDescription: 'Change or clear the filters to see more cases.',
      }
    : {
        kind: 'table',
        emptyTitle: 'No surgical cases scheduled',
        emptyDescription: 'Cases appear here once a procedure date is set.',
      };
}
